import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { MapPinIcon, PhoneIcon, EnvelopeIcon, GlobeAltIcon, AcademicCapIcon, BuildingOfficeIcon, BuildingLibraryIcon, XMarkIcon, ChevronDownIcon, CalendarIcon, ArrowPathIcon, ArrowTopRightOnSquareIcon, ArrowsRightLeftIcon, CheckIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { API_BASE_URL, getAuthHeaders } from '../config/api';
import { formatFeeDisplay, formatProgramDisplay, formatDateDisplay, formatRelativeTime, getShortSourceLabel, computeDynamicDeadlineStatus, DEGREE_GROUPS, getProgramDegreeGroup } from '../utils/formatters';
import { addToCompare, removeFromCompare, getCompare } from '../utils/compare';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import UniversityVerifiedBadge from '../components/university/UniversityVerifiedBadge';
import DakhlaButterflyLoader from '../components/common/DakhlaButterflyLoader';
import { useGlobalLoading } from '../contexts/LoadingContext';

// Static university data
// universitiesData removed - migrated to backend
const UniversityDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isFeatured = searchParams.get('featured') === 'true';
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedPrograms, setExpandedPrograms] = useState(new Set());
  const [programSearchQuery, setProgramSearchQuery] = useState('');
  const [selectedDegreeGroup, setSelectedDegreeGroup] = useState('all');

  const toggleProgramDetails = (key) => {
    setExpandedPrograms(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };
  const isProgrammaticScrollRef = useRef(false);
  const programmaticTimeoutRef = useRef(null);
  const [compareItems, setCompareItems] = useState(() => getCompare());

  useEffect(() => {
    const handleCompareUpdated = (e) => {
      setCompareItems(e.detail || getCompare());
    };
    window.addEventListener('compareUpdated', handleCompareUpdated);
    return () => window.removeEventListener('compareUpdated', handleCompareUpdated);
  }, []);

  const [university, setUniversity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isStaticUniversity, setIsStaticUniversity] = useState(false);
  const [coverImageError, setCoverImageError] = useState(false);
  const [coverImageLoaded, setCoverImageLoaded] = useState(false);

  const tabs = React.useMemo(() => [
    { id: 'overview', name: 'Overview' },
    { id: 'programs', name: 'Programs Offered' },
    { id: 'fees', name: 'Fee Structure' },
    { id: 'deadlines', name: 'Admission Schedule' },
    // Only show FAQs tab for partnered universities
    ...(!isStaticUniversity ? [{ id: 'faqs', name: 'FAQs' }] : []),
    { id: 'campus', name: 'Campus Info' }
  ], [isStaticUniversity]);

  const [programsLoaded, setProgramsLoaded] = useState(false);
  const [facilitiesLoaded, setFacilitiesLoaded] = useState(false);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [loadingFacilities, setLoadingFacilities] = useState(false);

  // Apply functionality state
  const [hasApplied, setHasApplied] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [showProgramModal, setShowProgramModal] = useState(false);
  const [showAdmissionQuestionsModal, setShowAdmissionQuestionsModal] = useState(false);
  const [admissionQuestions, setAdmissionQuestions] = useState([]);
  const [admissionResponses, setAdmissionResponses] = useState({});
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [submittingApplication, setSubmittingApplication] = useState(false);
  const [availablePrograms, setAvailablePrograms] = useState([]);
  const [expandedFacility, setExpandedFacility] = useState({});
  const [expandedFaq, setExpandedFaq] = useState({});

  const { user, token, isStudent } = useAuth();
  const { showSuccess, showError, showWarning, showInfo } = useToast();
  const navigate = useNavigate();

  const { groupedPrograms, totalProgramsCount, groupCounts, populatedGroupsCount } = React.useMemo(() => {
    const progs = university?.programs || [];
    const totalCount = progs.length;

    // Group every program by its degree tier key
    const rawGroups = {};
    DEGREE_GROUPS.forEach(g => {
      rawGroups[g.key] = [];
    });

    progs.forEach(prog => {
      const groupKey = getProgramDegreeGroup(prog);
      if (!rawGroups[groupKey]) {
        rawGroups[groupKey] = [];
      }
      rawGroups[groupKey].push(prog);
    });

    // Sort programs alphabetically within each group
    DEGREE_GROUPS.forEach(g => {
      if (rawGroups[g.key]) {
        rawGroups[g.key].sort((a, b) => {
          const nameA = (a.canonical_name || a.name || a.program_name || '').trim();
          const nameB = (b.canonical_name || b.name || b.program_name || '').trim();
          return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
        });
      }
    });

    // Count totals per group before query filtering
    const counts = {};
    DEGREE_GROUPS.forEach(g => {
      counts[g.key] = rawGroups[g.key]?.length || 0;
    });

    const populatedCount = DEGREE_GROUPS.filter(g => (counts[g.key] || 0) > 0).length;

    const query = programSearchQuery.trim().toLowerCase();

    // Assemble final filtered groups
    const resultGroups = DEGREE_GROUPS.map(groupMeta => {
      let items = rawGroups[groupMeta.key] || [];

      // Filter by degree level pill
      if (selectedDegreeGroup !== 'all' && selectedDegreeGroup !== groupMeta.key) {
        return { ...groupMeta, programs: [] };
      }

      // Filter by real-time search query
      if (query) {
        items = items.filter(p => {
          const pName = (p.canonical_name || p.name || p.program_name || '').toLowerCase();
          const pRaw = (p.raw_name || '').toLowerCase();
          const pDeg = (typeof p.degree === 'string' ? p.degree : p.degree_type?.name || '').toLowerCase();
          const pField = (typeof p.field_of_study === 'string' ? p.field_of_study : p.field_of_study?.name || '').toLowerCase();
          const shortCode = (p.canonical_program?.short_code || p.short_code || '').toLowerCase();
          const aliases = [
            ...(Array.isArray(p.canonical_program?.aliases) ? p.canonical_program.aliases : []),
            ...(Array.isArray(p.aliases) ? p.aliases : [])
          ];
          const matchesAlias = aliases.some(a => typeof a === 'string' && a.toLowerCase().includes(query));

          return (
            pName.includes(query) ||
            pRaw.includes(query) ||
            pDeg.includes(query) ||
            pField.includes(query) ||
            shortCode.includes(query) ||
            matchesAlias
          );
        });
      }

      return {
        ...groupMeta,
        programs: items,
        totalInGroup: rawGroups[groupMeta.key]?.length || 0,
      };
    }).filter(g => g.programs.length > 0);

    return {
      groupedPrograms: resultGroups,
      totalProgramsCount: totalCount,
      groupCounts: counts,
      populatedGroupsCount: populatedCount,
    };
  }, [university?.programs, programSearchQuery, selectedDegreeGroup]);

  const uniSchema = React.useMemo(() => {
    if (!university) return null;
    const schemaObj = {
      '@context': 'https://schema.org',
      '@type': 'CollegeOrUniversity',
      name: university.name,
      url: `https://dakhla.pk/university/${university.slug || university.id}`,
      description: university.about || `Explore ${university.name} programs, fee structure, admission deadlines, and apply online on Dakhla.`,
    };
    if (university.logo) {
      schemaObj.logo = university.logo;
    }
    if (university.location) {
      schemaObj.address = {
        '@type': 'PostalAddress',
        addressLocality: university.location,
        addressCountry: 'PK',
      };
    }
    return schemaObj;
  }, [university]);

  useDocumentMetadata(
    university ? `${university.name} Admissions 2026 - Programs, Fees & Deadlines | Dakhla` : 'University Details - Dakhla',
    university ? (university.about || `Learn about ${university.name} admission process, courses, fees, and campus details on Dakhla.`) : 'Browse university profiles and details on Dakhla.',
    university?.logo || university?.coverImage || null,
    uniSchema
  );

  const handleImageRef = (node) => {
    if (node && node.complete) {
      if (node.naturalWidth === 0) {
        setCoverImageError(true);
        setCoverImageLoaded(false);
      } else {
        setCoverImageLoaded(true);
      }
    }
  };

  useEffect(() => {
    setCoverImageError(false);
    setCoverImageLoaded(false);
  }, [id, university?.coverImage, university?.cover_photo]);

  // Check application status on mount for authenticated students
  useEffect(() => {
    if (isStudent && token && university?.id) {
      checkApplicationStatus();
    }
  }, [isStudent, token, university?.id]);

  const checkApplicationStatus = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/application/check-status?university_id=${university.id}`,
        {
          headers: getAuthHeaders(token)
        }
      );

      if (response.ok) {
        const data = await response.json();
        setHasApplied(data.has_applied || false);
        setApplicationStatus(data.status);
      }
    } catch (err) {
      console.error('Error checking application status:', err);
    }
  };

  const handleApplyClick = async () => {
    if (!isStudent) {
      showError('Only students can apply to universities');
      return;
    }

    if (!token) {
      navigate('/login');
      return;
    }

    // Load available programs for this university
    try {
      const response = await fetch(
        `${API_BASE_URL}/university/${university.id}/programs`,
        {
          headers: getAuthHeaders(token)
        }
      );

      if (response.ok) {
        const programs = await response.json();
        setAvailablePrograms(programs);
        setShowProgramModal(true);
      } else {
        showError('Failed to load programs');
      }
    } catch (err) {
      console.error('Error loading programs:', err);
      showError('Failed to load programs');
    }
  };

  const fetchAdmissionQuestions = async () => {
    try {
      setLoadingQuestions(true);
      const response = await fetch(
        `${API_BASE_URL}/student/admission-questions/${university.id}`,
        {
          headers: getAuthHeaders(token)
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAdmissionQuestions(data.questions || []);
        return data.questions || [];
      } else if (response.status === 404) {
        // No questions for this university
        setAdmissionQuestions([]);
        return [];
      } else {
        showError('Failed to load admission questions');
        return [];
      }
    } catch (err) {
      console.error('Error loading admission questions:', err);
      return [];
    } finally {
      setLoadingQuestions(false);
    }
  };

  const handleProgramSelected = async () => {
    if (!selectedProgram) {
      showError('Please select a program');
      return;
    }

    // Close program modal
    setShowProgramModal(false);

    // Fetch admission questions for this university
    const questions = await fetchAdmissionQuestions();

    // If there are questions, show the admission questions modal
    if (questions.length > 0) {
      setShowAdmissionQuestionsModal(true);
    } else {
      // No questions, proceed directly to submission
      await submitApplication();
    }
  };

  const handleAdmissionResponseChange = (questionId, value) => {
    setAdmissionResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const validateAdmissionResponses = () => {
    const errors = [];

    admissionQuestions.forEach(question => {
      if (question.is_required) {
        const response = admissionResponses[question.id];
        if (!response || response.trim() === '') {
          errors.push(`${question.question_label} is required`);
        }
      }
    });

    return errors;
  };

  const handleAdmissionQuestionsSubmit = async () => {
    // Validate responses
    const errors = validateAdmissionResponses();
    if (errors.length > 0) {
      showError(errors[0]);
      return;
    }

    // Close modal and submit application
    setShowAdmissionQuestionsModal(false);
    await submitApplication();
  };

  const submitApplication = async () => {
    setSubmittingApplication(true);

    try {
      // Prepare responses array
      const responsesArray = Object.entries(admissionResponses).map(([qId, val]) => ({
        question_id: parseInt(qId),
        response_value: val
      }));

      const response = await fetch(`${API_BASE_URL}/application/submit`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          program_id: selectedProgram.id,
          university_id: university.id,
          additional_admission_responses: responsesArray
        })
      });

      const data = await response.json();

      if (response.status === 201) {
        // Success
        showSuccess('Application submitted successfully!');
        setHasApplied(true);
        setApplicationStatus('Pending');
        setSelectedProgram(null);
        setAdmissionResponses({});
        setAdmissionQuestions([]);
      } else if (response.status === 400) {
        // Profile incomplete
        showError(data.message || 'Please complete your profile before applying');
        navigate('/application-form');
      } else if (response.status === 409) {
        // Duplicate application
        showError(data.message || 'You have already applied to this program');
        setSelectedProgram(null);
      } else if (response.status === 404) {
        // Not found
        showError(data.message || 'Program or university not found');
      } else {
        // Other errors
        showError(data.message || 'Failed to submit application');
      }
    } catch (err) {
      console.error('Error submitting application:', err);
      showError('Failed to submit application. Please try again.');
    } finally {
      setSubmittingApplication(false);
    }
  };

  const handleSubmitApplication = async () => {
    await handleProgramSelected();
  };

  const handleTabClick = (tabId) => {
    isProgrammaticScrollRef.current = true;
    setActiveTab(tabId);

    if (programmaticTimeoutRef.current) {
      clearTimeout(programmaticTimeoutRef.current);
    }

    const element = document.getElementById(tabId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });

      programmaticTimeoutRef.current = setTimeout(() => {
        isProgrammaticScrollRef.current = false;
      }, 800);
    } else {
      isProgrammaticScrollRef.current = false;
    }
  };

  const fetchPrograms = async () => {
    if (programsLoaded || isStaticUniversity || !university?.id) return;

    try {
      setLoadingPrograms(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/university/${university.id}/programs`);

      if (response.ok) {
        const programs = await response.json();
        const formattedPrograms = programs.map(p => {
          const canonName = p.canonical_name || (p.canonical_program ? p.canonical_program.name : null);
          const standardName = canonName || p.name || p.program_name;
          const rawName = p.raw_name || p.program_name || p.name;
          return {
            id: p.id,
            name: standardName,
            canonical_name: canonName,
            raw_name: rawName,
            degree: p.degree_type || p.degree,
            duration: p.duration,
            fee: p.fee || p.fee_structure || null,
            fee_type: p.fee_type,
            description: p.description,
            deadline: p.deadline || p.application_deadline,
            status: p.status || p.deadline_status,
            requirements: p.requirements,
            intake_session: p.intake_session,
            min_eligibility_percentage: p.min_eligibility_percentage,
            required_entry_test: p.required_entry_test,
            source_url: p.source_url
          };
        });

        setUniversity(prev => ({
          ...prev,
          programs: formattedPrograms,
          deadlines: formattedPrograms.map(p => ({
            program: p.name,
            degree: p.degree,
            deadline: p.deadline,
            status: p.deadline && new Date(p.deadline) > new Date() ? 'Open' : 'Closed'
          }))
        }));
        setProgramsLoaded(true);
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
    } finally {
      setLoadingPrograms(false);
    }
  };

  const fetchFacilities = async () => {
    if (facilitiesLoaded || isStaticUniversity || !university?.id) return;

    try {
      setLoadingFacilities(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/university/${university.id}/facilities`);

      if (response.ok) {
        const facilities = await response.json();
        setUniversity(prev => ({
          ...prev,
          campus_facilities: facilities
        }));
        setFacilitiesLoaded(true);
      }
    } catch (error) {
      console.error('Error fetching facilities:', error);
    } finally {
      setLoadingFacilities(false);
    }
  };

  // Load programs and facilities when university is loaded
  useEffect(() => {
    if (university && !isStaticUniversity) {
      if (!programsLoaded) fetchPrograms();
      if (!facilitiesLoaded) fetchFacilities();
    }
  }, [university, programsLoaded, facilitiesLoaded, isStaticUniversity]);

  // Scroll Spy to update activeTab on scroll
  useEffect(() => {
    let isScrolling = false;
    const handleScroll = () => {
      if (isProgrammaticScrollRef.current) {
        if (programmaticTimeoutRef.current) {
          clearTimeout(programmaticTimeoutRef.current);
        }
        programmaticTimeoutRef.current = setTimeout(() => {
          isProgrammaticScrollRef.current = false;
        }, 100);
        return;
      }

      if (isScrolling) return;
      isScrolling = true;
      requestAnimationFrame(() => {
        const scrollPosition = window.scrollY + 135; // Offset for sticky headers

        let currentTab = tabs[0]?.id;
        for (const tab of tabs) {
          const element = document.getElementById(tab.id);
          if (element) {
            const top = element.offsetTop;
            if (scrollPosition >= top) {
              currentTab = tab.id;
            }
          }
        }

        if (currentTab && currentTab !== activeTab) {
          setActiveTab(currentTab);
        }
        isScrolling = false;
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (programmaticTimeoutRef.current) {
        clearTimeout(programmaticTimeoutRef.current);
      }
    };
  }, [activeTab, tabs]);

  const fetchUniversityDetail = async () => {
    try {
      setLoading(true);
      setError(null);

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/university/${id}`);

      if (response.ok) {
        const data = await response.json();
        setIsStaticUniversity(!data.is_partner);

        // If accessed via a historical slug alias, seamlessly update the browser URL
        const canonicalSlug = data.slug || data.canonical_slug;
        if (canonicalSlug && canonicalSlug !== id && !id.match(/^\d+$/)) {
          navigate(`/university/${canonicalSlug}`, { replace: true });
        }

        const transformedData = {
          ...data,
          name: data.name || data.university_name,
          location: data.location || (data.city && data.province ? `${data.city}, ${data.province}` : (data.city || data.address)),
          address: data.address || data.location,
          logo: data.profile_picture || data.logo,
          coverImage: data.cover_photo || data.coverImage,
          established: data.established_year || data.established,
          type: data.university_type || data.type,
          website: data.website_url || data.website,
          phone: data.phone || data.phone_number,
          email: data.email,
          about: data.about_me || data.about_university || data.campus_info || data.about,
          programs: (data.programs || []).map(p => {
            const canonName = p.canonical_name || (p.canonical_program ? p.canonical_program.name : null);
            return {
              ...p,
              name: canonName || p.name || p.program_name,
              canonical_name: canonName,
              raw_name: p.raw_name || p.program_name || p.name,
            };
          }),
          feeStructure: data.fee_structure || data.feeStructure || [],
          faqs: data.faqs || [],
          facilities: data.facilities || [],
          campus_facilities: data.campus_facilities || [],
          deadlines: data.deadlines || []
        };

        setUniversity(transformedData);
        setLoading(false);
        return;
      }

      throw new Error('University not found');

    } catch (err) {
      console.error('Error fetching university details:', err);
      setError('Failed to load university details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchUniversityDetail();
    } else {
      setError('No university ID provided');
      setLoading(false);
    }
  }, [id]);

  const { showLoader, hideLoader } = useGlobalLoading();

  useEffect(() => {
    if (loading) {
      showLoader("Loading University Profile...");
    } else {
      hideLoader();
    }
  }, [loading, showLoader, hideLoader]);

  if (loading) {
    return null;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Error Loading University</h2>
          <p className="text-slate-600 mb-6">{error}</p>
          <Link to="/" className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  if (!university) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">University Not Found</h2>
          <p className="text-slate-600 mb-6">The requested university could not be found.</p>
          <Link to="/" className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const rawCoverUrl = university?.coverImage || university?.cover_photo;
  const coverUrl = rawCoverUrl
    ? (rawCoverUrl.startsWith('/static/')
      ? `${(process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '')}${rawCoverUrl}`
      : rawCoverUrl)
    : null;
  const hasValidCover = coverUrl && !coverImageError;
  const showCoverImage = hasValidCover && coverImageLoaded;

  return (
    <div className="min-h-screen bg-slate-100 pb-16">
      {/* Header Section */}
      <div
        className={`relative text-white overflow-hidden shadow-sm transition-colors duration-500 ${showCoverImage
            ? 'bg-slate-900'
            : 'bg-[#1E256F]'
          }`}
      >
        {/* Background image if valid cover */}
        {hasValidCover && (
          <img
            key={coverUrl}
            ref={handleImageRef}
            src={coverUrl}
            alt=""
            role="presentation"
            className={`absolute inset-0 w-full h-full object-cover pointer-events-none transition-opacity duration-500 ${coverImageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
            onLoad={() => setCoverImageLoaded(true)}
            onError={() => {
              setCoverImageError(true);
              setCoverImageLoaded(false);
            }}
          />
        )}

        {/* Subtle grid pattern overlay */}
        <div
          className="absolute top-0 left-0 w-full h-full pointer-events-none"
          style={{
            backgroundImage: "linear-gradient(to right, rgba(255, 255, 255, 0.07) 1px, transparent 1px), linear-gradient(to bottom, rgba(255, 255, 255, 0.07) 1px, transparent 1px)",
            backgroundSize: "24px 24px"
          }}
        />

        {/* Clean dark gradient overlay when cover image exists and is loaded */}
        {hasValidCover && (
          <div className={`transition-opacity duration-500 ${coverImageLoaded ? 'opacity-100' : 'opacity-0'}`}>
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20 pointer-events-none" />
            <div className="hidden lg:block absolute inset-0 bg-gradient-to-r from-black/65 via-black/20 to-transparent pointer-events-none" />
          </div>
        )}

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 z-10">
          {/* Multi-Campus Switcher Bar */}
          {university.institution_group && university.institution_group.campuses && university.institution_group.campuses.length > 1 && (
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-3 mb-6 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <BuildingLibraryIcon className="h-4 w-4 text-indigo-300 flex-shrink-0" />
                <span className="text-xs font-bold uppercase tracking-wider text-slate-200">
                  {university.institution_group.short_name || university.institution_group.name} Campuses ({university.institution_group.campuses.length}):
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {university.institution_group.campuses.map(campus => {
                  const isCurrent = campus.id === university.id || (campus.slug && campus.slug === university.slug);
                  return (
                    <Link
                      key={campus.id}
                      to={`/university/${campus.slug || campus.id}`}
                      className={`px-3 py-1 rounded-xl text-xs font-bold transition-all ${isCurrent
                          ? 'bg-white text-indigo-900 shadow-md scale-105'
                          : 'bg-white/15 text-white hover:bg-white/25 border border-white/20'
                        }`}
                    >
                      {campus.campus_name || campus.city_name || campus.name}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Mobile Layout */}
          <div className="block lg:hidden">
            <div className="flex flex-col space-y-5">
              {/* Logo and Basic Info */}
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <img
                    src={
                      university.logo
                        ? (university.logo.startsWith('/static/') ? `${(process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '')}${university.logo}` : university.logo)
                        : (university.profile_picture || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjRjNGNEY2Ii8+Cjx0ZXh0IHg9IjUwIiB5PSI1NSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE0IiBmaWxsPSIjNkI3MjgwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5Mb2dvPC90ZXh0Pgo8L3N2Zz4K')
                    }
                    alt={university.name || university.university_name}
                    width="80"
                    height="80"
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl border-4 border-white/90 shadow-xl bg-white object-contain p-2 hover:scale-105 transition-premium"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjRjNGNEY2Ii8+Cjx0ZXh0IHg9IjUwIiB5PSI1NSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE0IiBmaWxsPSIjNkI3MjgwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5Mb2dvPC90ZXh0Pgo8L3N2Zz4K';
                    }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-xl sm:text-2xl font-bold leading-tight mb-1.5 break-words tracking-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
                    <span>{university.name || university.university_name}</span>
                    <span className="inline-flex items-center ml-2 align-middle">
                      <UniversityVerifiedBadge isPartner={!isStaticUniversity} className="h-5 w-5 sm:h-6 sm:w-6" />
                    </span>
                  </h1>
                  <div className="flex items-center text-sm mb-1 text-slate-300 font-medium drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
                    <MapPinIcon className="h-4 w-4 mr-1.5 flex-shrink-0" />
                    <span className="truncate">{university.location || university.city || university.address}</span>
                  </div>
                </div>
              </div>

              {/* University Details */}
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="bg-white/15 border border-white/25 px-3 py-1 rounded-xl font-bold text-white">Est. {university.established}</span>
                <span className="bg-white/15 border border-white/25 px-3 py-1 rounded-xl font-bold text-white">{university.type} University</span>
                <span className="bg-white/15 border border-white/25 px-3 py-1 rounded-xl font-bold text-white">HEC Recognized</span>
              </div>

              {/* Status Card */}
              {isStaticUniversity && (
                <div className="bg-white/15 border border-white/25 rounded-2xl p-4 shadow-xl max-w-sm flex-shrink-0">
                  <div className="flex items-center mb-2">
                    <span className="h-2 w-2 rounded-full bg-slate-300 mr-2" />
                    <span className="text-xs font-bold text-white uppercase tracking-wider">Directory Profile</span>
                  </div>
                  <p className="text-xs text-white/80 leading-relaxed mb-3">
                    Independent directory profile for informational reference. Logos and marks belong to the institution.
                  </p>
                  {(university.website_url || university.website) ? (
                    <a
                      href={university.website_url || university.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center w-full bg-white/20 hover:bg-white/30 text-white py-2 px-3 rounded-xl text-xs font-bold transition-colors border border-white/30"
                    >
                      Visit Official Portal ↗
                    </a>
                  ) : (
                    <p className="text-[11px] text-slate-300 italic">Apply directly via official university admissions portal.</p>
                  )}
                </div>
              )}
              {!isStaticUniversity && (
                <div className="bg-primary-950/45 border border-primary-500/30 rounded-2xl p-4 shadow-2xl max-w-sm flex-shrink-0">
                  <div className="flex items-center mb-2">
                    <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse mr-2" />
                    <span className="text-xs font-bold text-emerald-300 uppercase tracking-wider">Verified Official Partner</span>
                  </div>
                  <p className="text-xs text-white/80 leading-relaxed mb-3">
                    Official partner institution. Direct application portal launching soon.
                  </p>
                  <button
                    disabled
                    className="w-full bg-slate-800/60 text-slate-400 py-2 px-3 rounded-xl text-xs font-bold text-center border border-slate-700/30 cursor-not-allowed shadow-none"
                  >
                    Direct Apply Coming Soon
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:block">
            <div className="flex items-start space-x-8">
              <div className="flex-shrink-0">
                <img
                  src={
                    university.logo
                      ? (university.logo.startsWith('/static/') ? `${(process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '')}${university.logo}` : university.logo)
                      : (university.profile_picture || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjRjNGNEY2Ii8+Cjx0ZXh0IHg9IjUwIiB5PSI1NSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE0IiBmaWxsPSIjNkI3MjgwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5Mb2dvPC90ZXh0Pgo8L3N2Zz4K')
                  }
                  alt={university.name || university.university_name}
                  width="112"
                  height="112"
                  className="w-28 h-28 rounded-2xl border-4 border-white/90 shadow-xl bg-white object-contain p-2 hover:scale-105 transition-premium"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjRjNGNEY2Ii8+Cjx0ZXh0IHg9IjUwIiB5PSI1NSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE0IiBmaWxsPSIjNkI3MjgwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5Mb2dvPC90ZXh0Pgo8L3N2Zz4K';
                  }}
                />
              </div>
              <div className="flex-1">
                <div className="text-white">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h1 className="text-3xl lg:text-4xl font-extrabold leading-tight mb-2 tracking-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
                        <span>{university.name || university.university_name}</span>
                        <span className="inline-flex items-center ml-2.5 align-middle">
                          <UniversityVerifiedBadge isPartner={!isStaticUniversity} className="h-6 w-6 lg:h-7 lg:w-7" />
                        </span>
                      </h1>
                      <div className="flex items-center text-lg mb-3 text-slate-300 font-medium drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
                        <MapPinIcon className="h-5 w-5 mr-2" />
                        <span>{university.location || university.city || university.address}</span>
                      </div>
                      <div className="flex items-center space-x-6 text-sm">
                        <span className="bg-white/15 border border-white/25 px-3 py-1 rounded-xl font-bold">Est. {university.established}</span>
                        <span className="bg-white/15 border border-white/25 px-3 py-1 rounded-xl font-bold">{university.type} University</span>
                        <span className="bg-white/15 border border-white/25 px-3 py-1 rounded-xl font-bold">HEC Recognized</span>
                      </div>
                    </div>

                    {isStaticUniversity && (
                      <div className="ml-6 bg-white/15 border border-white/25 rounded-2xl p-4 shadow-xl max-w-sm flex-shrink-0">
                        <div className="flex items-center mb-2">
                          <span className="h-2 w-2 rounded-full bg-slate-300 mr-2" />
                          <span className="text-xs font-bold text-white uppercase tracking-wider">Directory Profile</span>
                        </div>
                        <p className="text-xs text-white/80 leading-relaxed mb-3">
                          Independent directory profile for informational reference. Logos and marks belong to the institution.
                        </p>
                        {(university.website_url || university.website) ? (
                          <a
                            href={university.website_url || university.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center w-full bg-white/20 hover:bg-white/30 text-white py-2 px-3 rounded-xl text-xs font-bold transition-colors border border-white/30"
                          >
                            Visit Official Portal ↗
                          </a>
                        ) : (
                          <p className="text-[11px] text-slate-300 italic">Apply directly via official university admissions portal.</p>
                        )}
                      </div>
                    )}
                    {!isStaticUniversity && (
                      <div className="ml-6 bg-primary-950/45 border border-primary-500/30 rounded-2xl p-5 shadow-2xl max-w-sm flex-shrink-0">
                        <div className="flex items-center mb-2">
                          <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse mr-2" />
                          <span className="text-xs font-bold text-emerald-300 uppercase tracking-wider">Verified Official Partner</span>
                        </div>
                        <p className="text-xs text-white/80 leading-relaxed mb-4">
                          Official partner institution. Direct application portal launching soon.
                        </p>
                        <button
                          disabled
                          className="w-full bg-slate-800/60 text-slate-400 py-2.5 px-4 rounded-xl text-sm font-bold text-center border border-slate-700/30 cursor-not-allowed shadow-none"
                        >
                          Direct Apply Coming Soon
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-slate-200 sticky z-20 transition-all duration-300 relative" style={{ top: 'var(--navbar-height, 64px)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-4 sm:space-x-8 overflow-x-auto scrollbar-hide -mb-px pr-16">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  className={`py-3.5 px-1 border-b-2 text-sm font-bold whitespace-nowrap transition-all duration-300 ${isActive
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-slate-600 hover:text-primary-600 hover:border-slate-200'
                    }`}
                >
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>
        {/* Right-side glow indicator for horizontal scrollability */}
        <div className="absolute right-0 top-0 bottom-[1px] w-16 pointer-events-none bg-gradient-to-l from-white via-white/80 to-transparent z-30" />
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Main Content Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Overview Section */}
            <div id="overview" className="scroll-mt-32 space-y-6">
              {/* About Section */}
              <div className="card shadow-sm">
                <h3 className="text-xl font-extrabold text-slate-800 mb-4">About {university.name}</h3>
                <p className="text-slate-600 leading-relaxed text-sm sm:text-base">{university.about}</p>
              </div>
            </div>  {/* Programs Offered Section */}
            <div id="programs" className="scroll-mt-32 card shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
                <div className="flex items-center gap-2.5">
                  <h3 className="text-xl font-extrabold text-slate-800">Programs Offered</h3>
                </div>

                {/* Program Search Bar */}
                {totalProgramsCount > 4 && (
                  <div className="relative w-full sm:w-72">
                    <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={programSearchQuery}
                      onChange={(e) => setProgramSearchQuery(e.target.value)}
                      placeholder="Search programs by title or field..."
                      className="w-full pl-9 pr-8 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
                    />
                    {programSearchQuery && (
                      <button
                        type="button"
                        onClick={() => setProgramSearchQuery('')}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 rounded cursor-pointer"
                        title="Clear search"
                      >
                        <XMarkIcon className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Degree Group Filter Pills */}
              {totalProgramsCount > 0 && populatedGroupsCount > 1 && (
                <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1 mb-6">
                  <button
                    type="button"
                    onClick={() => setSelectedDegreeGroup('all')}
                    className={`px-3.5 py-1.5 rounded-full text-xs transition-all duration-150 whitespace-nowrap cursor-pointer active:scale-[0.98] bg-white border ${selectedDegreeGroup === 'all'
                        ? 'border-primary-600 text-primary-600 font-semibold shadow-xs'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:text-slate-900 hover:bg-slate-50 shadow-2xs font-medium'
                      }`}
                  >
                    <span>All</span>
                  </button>

                  {DEGREE_GROUPS.map((group) => {
                    const count = groupCounts[group.key] || 0;
                    if (count === 0) return null;
                    const isSelected = selectedDegreeGroup === group.key;

                    return (
                      <button
                        key={group.key}
                        type="button"
                        onClick={() => setSelectedDegreeGroup(isSelected ? 'all' : group.key)}
                        className={`px-3.5 py-1.5 rounded-full text-xs transition-all duration-150 whitespace-nowrap cursor-pointer active:scale-[0.98] bg-white border ${isSelected
                            ? 'border-primary-600 text-primary-600 font-semibold shadow-xs'
                            : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:text-slate-900 hover:bg-slate-50 shadow-2xs font-medium'
                          }`}
                      >
                        <span>{group.title}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {loadingPrograms ? (
                <div className="flex justify-center py-6">
                  <DakhlaButterflyLoader size="sm" text="Loading Programs..." />
                </div>
              ) : (
                <>
                  {/* Render Grouped Programs */}
                  {groupedPrograms.length > 0 && (
                    <div className="space-y-6">
                      {groupedPrograms.map((group) => (
                        <div key={group.key} className="space-y-3 pt-1 first:pt-0">
                          {/* Group Header */}
                          <div className="flex items-center gap-2.5 pb-2 border-b border-slate-100">
                            <h4 className="text-sm sm:text-base font-bold text-slate-800 tracking-tight">
                              {group.title}
                            </h4>
                            <span className="text-xs font-bold text-slate-600 bg-slate-100 border border-slate-200/80 px-2.5 py-0.5 rounded-full">
                              {group.programs.length} {group.programs.length === 1 ? 'Program' : 'Programs'}
                            </span>
                          </div>

                          {/* Programs List in this Group */}
                          <div className="flex flex-col space-y-3.5">
                            {group.programs.map((program, pIndex) => {
                              const progName = (program.name || program.program_name || '').toLowerCase();
                              const progDegree = (program.degree || program.degree_type || '').toUpperCase();

                              let deadlineEntry = null;
                              if (university.deadlines && university.deadlines.length > 0) {
                                deadlineEntry = university.deadlines.find(d => {
                                  const dp = (d.program || '').toLowerCase();
                                  return dp === progName || (dp.length > 3 && (progName.includes(dp) || dp.includes(progName)));
                                });

                                if (!deadlineEntry) {
                                  const isUg = ['BS', 'BE', 'BBA', 'BACHELOR', 'BSC', 'BA'].some(d => progDegree.includes(d)) ||
                                    ['bs', 'be', 'bba', 'bachelor'].some(d => progName.startsWith(d) || progName.includes(` ${d} `));
                                  const isPg = ['MS', 'ME', 'MPHIL', 'MBA', 'PHD', 'MASTER', 'DOCTOR'].some(d => progDegree.includes(d)) ||
                                    ['ms', 'me', 'mphil', 'mba', 'phd', 'master'].some(d => progName.startsWith(d) || progName.includes(` ${d} `));

                                  if (isUg) {
                                    deadlineEntry = university.deadlines.find(d => {
                                      const dp = (d.program || '').toLowerCase();
                                      return dp.includes('undergrad') || dp.includes('bachelor') || dp.includes('bs') || dp.includes('be') || dp.includes('bba');
                                    });
                                  } else if (isPg) {
                                    deadlineEntry = university.deadlines.find(d => {
                                      const dp = (d.program || '').toLowerCase();
                                      return dp.includes('postgrad') || dp.includes('grad') || dp.includes('master') || dp.includes('ms') || dp.includes('mphil') || dp.includes('mba') || dp.includes('phd');
                                    });
                                  }
                                }
                              }

                              const effectiveDeadline = program.deadline || deadlineEntry?.deadline;
                              const deadlineStatus = computeDynamicDeadlineStatus(effectiveDeadline, deadlineEntry?.status || program.deadline_status || program.status);

                              let statusBadge = null;
                              if (deadlineStatus && deadlineStatus !== 'TBA') {
                                const isOp = deadlineStatus.toLowerCase() === 'open';
                                const isUp = deadlineStatus.toLowerCase() === 'upcoming';
                                if (isOp || isUp) {
                                  statusBadge = (
                                    <span className={`${isOp
                                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                                        : 'bg-amber-50 text-amber-700 border border-amber-200'
                                      } text-xs font-semibold px-2.5 py-0.5 rounded-lg shrink-0`}>
                                      {deadlineStatus}
                                    </span>
                                  );
                                }
                              }

                              const programDescription = program.description || program.program_description || program.canonical_program?.description;
                              const hasRealDescription = Boolean(programDescription && !programDescription.startsWith('Degree:'));
                              const rawDuration = program.duration || (
                                program.canonical_program?.duration_years
                                  ? `${program.canonical_program.duration_years} ${program.canonical_program.duration_years === 1 ? 'Year' : 'Years'}`
                                  : null
                              );
                              const programDuration = rawDuration && rawDuration !== 'N/A'
                                ? (/^\d+(\.\d+)?$/.test(String(rawDuration).trim())
                                    ? `${rawDuration} Years`
                                    : String(rawDuration).replace(/\byear\b/gi, 'Year').replace(/\byears\b/gi, 'Years'))
                                : null;
                              const programKey = program.id ? String(program.id) : `${group.key}-${program.name || program.program_name || 'p'}-${pIndex}`;
                              const isExpanded = expandedPrograms.has(programKey);

                              return (
                                <div
                                  key={programKey}
                                  onClick={() => toggleProgramDetails(programKey)}
                                  className="border border-slate-200 rounded-2xl p-4 sm:px-5 sm:py-3.5 bg-white hover:border-slate-300 hover:shadow-xs transition-all duration-200 cursor-pointer"
                                >
                                  {/* First line: Program Name & Duration (left), Status Badge & Dropdown Icon (right) */}
                                  <div className="flex items-center justify-between gap-3 select-none">
                                    <div className="min-w-0 pr-2">
                                      <div className="flex items-baseline gap-1.5 flex-wrap">
                                        <h4 className="font-medium text-slate-900 text-sm sm:text-base leading-snug">
                                          {formatProgramDisplay(program.canonical_name || program.name || program.program_name, program.degree || program.degree_type)}
                                        </h4>
                                        {programDuration && (
                                          <span className="text-xs sm:text-sm font-normal text-slate-400 shrink-0">
                                            ({programDuration})
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2.5 sm:gap-3 shrink-0">
                                      {statusBadge}
                                      <ChevronDownIcon
                                        className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-180 text-slate-600' : ''
                                          }`}
                                      />
                                    </div>
                                  </div>

                                  {/* Expandable Details Block */}
                                  {isExpanded && (
                                    <div
                                      className="mt-4 pt-4 border-t border-slate-100 space-y-4 cursor-default"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {/* About Program (Top of dropdown) */}
                                      {hasRealDescription && (
                                        <div>
                                          <h5 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                                            About Program
                                          </h5>
                                          <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
                                            {programDescription}
                                          </p>
                                        </div>
                                      )}

                                      {/* Group 1: Key Information */}
                                      <div className={`${hasRealDescription ? 'pt-3 border-t border-slate-100/80' : ''}`}>
                                        <h5 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2.5">
                                          Key Information
                                        </h5>
                                        <div className="space-y-2">
                                          <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4">
                                            <span className="w-36 sm:w-44 shrink-0 text-xs sm:text-sm font-medium text-slate-500">
                                              Duration
                                            </span>
                                            <span className="text-xs sm:text-sm font-semibold text-slate-900">
                                              {program.duration || 'N/A'}
                                            </span>
                                          </div>

                                          <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4">
                                            <span className="w-36 sm:w-44 shrink-0 text-xs sm:text-sm font-medium text-slate-500">
                                              Tuition Fee
                                            </span>
                                            <span className="text-xs sm:text-sm font-semibold text-slate-900">
                                              {formatFeeDisplay(program.fee || program.fee_structure, program.fee_type || 'semester')}
                                            </span>
                                          </div>

                                          <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4">
                                            <span className="w-36 sm:w-44 shrink-0 text-xs sm:text-sm font-medium text-slate-500">
                                              Application Deadline
                                            </span>
                                            <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm">
                                              <span className="font-semibold text-slate-900">
                                                {effectiveDeadline ? formatDateDisplay(effectiveDeadline) : 'TBA'}
                                              </span>
                                              {program.intake_session && (
                                                <span className="text-slate-500 font-normal">
                                                  ({program.intake_session})
                                                </span>
                                              )}
                                              {deadlineStatus && deadlineStatus !== 'TBA' && (
                                                <span className={`text-xs font-semibold px-2 py-0.5 rounded-md ${deadlineStatus.toLowerCase() === 'open'
                                                    ? 'text-emerald-700 bg-emerald-50 border border-emerald-100'
                                                    : deadlineStatus.toLowerCase() === 'upcoming'
                                                      ? 'text-amber-700 bg-amber-50 border border-amber-200'
                                                      : 'text-rose-600 bg-rose-50 border border-rose-100'
                                                  }`}>
                                                  {deadlineStatus}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Group 2: Additional Fees (Only if present) */}
                                      {(program.admission_fee_one_time || program.security_deposit_refundable) && (
                                        <div className="pt-3 border-t border-slate-100/80">
                                          <h5 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2.5">
                                            Additional Fees
                                          </h5>
                                          <div className="space-y-2">
                                            {program.admission_fee_one_time && (
                                              <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4">
                                                <span className="w-36 sm:w-44 shrink-0 text-xs sm:text-sm font-medium text-slate-500">
                                                  Admission Fee
                                                </span>
                                                <span className="text-xs sm:text-sm text-slate-800">
                                                  PKR {Number(program.admission_fee_one_time).toLocaleString()} <span className="text-slate-400 font-normal">(One-time)</span>
                                                </span>
                                              </div>
                                            )}
                                            {program.security_deposit_refundable && (
                                              <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4">
                                                <span className="w-36 sm:w-44 shrink-0 text-xs sm:text-sm font-medium text-slate-500">
                                                  Security Deposit
                                                </span>
                                                <span className="text-xs sm:text-sm text-slate-800">
                                                  PKR {Number(program.security_deposit_refundable).toLocaleString()} <span className="text-slate-400 font-normal">(Refundable)</span>
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                      {/* Group 3: Admission Criteria (Only if present) */}
                                      {(program.min_eligibility_percentage || program.required_entry_test || program.requirements || program.program_requirements) && (
                                        <div className="pt-3 border-t border-slate-100/80">
                                          <h5 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2.5">
                                            Admission Criteria
                                          </h5>
                                          <div className="space-y-2">
                                            {program.min_eligibility_percentage && (
                                              <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4">
                                                <span className="w-36 sm:w-44 shrink-0 text-xs sm:text-sm font-medium text-slate-500">
                                                  Min. Eligibility
                                                </span>
                                                <span className="text-xs sm:text-sm text-slate-800">
                                                  {program.min_eligibility_percentage}% in Intermediate / HSSC or equivalent
                                                </span>
                                              </div>
                                            )}
                                            {program.required_entry_test && (
                                              <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4">
                                                <span className="w-36 sm:w-44 shrink-0 text-xs sm:text-sm font-medium text-slate-500">
                                                  Accepted Tests
                                                </span>
                                                <span className="text-xs sm:text-sm text-slate-800">
                                                  {program.required_entry_test}
                                                </span>
                                              </div>
                                            )}
                                            {(program.requirements || program.program_requirements) && (
                                              <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4">
                                                <span className="w-36 sm:w-44 shrink-0 text-xs sm:text-sm font-medium text-slate-500">
                                                  Prerequisites
                                                </span>
                                                <span className="text-xs sm:text-sm text-slate-800 leading-relaxed">
                                                  {program.requirements || program.program_requirements}
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                      {/* Note (Subtle grey box with matching group header) */}
                                      {program.note && (
                                        <div className="bg-slate-50/80 rounded-xl px-3.5 py-2.5 border border-slate-200/60 text-xs sm:text-sm">
                                          <h5 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">
                                            Note
                                          </h5>
                                          <p className="text-slate-700 leading-relaxed">
                                            {program.note}
                                          </p>
                                        </div>
                                      )}

                                      {/* Footer bar: Compare Button & Source / Updated info */}
                                      <div className="pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
                                        {(() => {
                                          const programId = program.id;
                                          const programName = program.canonical_name || program.name || program.program_name;
                                          const programType = isStaticUniversity ? 'static' : 'partner';
                                          const isProgramInCompare = compareItems.some(
                                            item => (String(item.id) === String(programId) && item.type === programType) ||
                                              item.uniqueId === `${programType}:${programId}`
                                          );
                                          const isTrayFull = compareItems.length >= 4 && !isProgramInCompare;

                                          return (
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (!programId) {
                                                  showError('Program ID not found. Please refresh the page.');
                                                  return;
                                                }

                                                if (isProgramInCompare) {
                                                  removeFromCompare(programId, programType);
                                                  showInfo('Removed from comparison');
                                                  return;
                                                }

                                                if (compareItems.length >= 4) {
                                                  showWarning('Comparison limit reached (max 4 programs)');
                                                  return;
                                                }

                                                addToCompare({
                                                  id: programId,
                                                  type: programType,
                                                  program_name: programName,
                                                  university: {
                                                    id: university.id,
                                                    name: university.name || university.university_name,
                                                    profile_picture: university.profile_picture || university.logo,
                                                    website: university.website || university.website_link || null
                                                  }
                                                });
                                                showSuccess('Added to comparison');
                                              }}
                                              disabled={isTrayFull}
                                              title={
                                                isProgramInCompare
                                                  ? 'Click to remove from comparison'
                                                  : isTrayFull
                                                    ? 'Comparison limit reached (max 4 programs)'
                                                    : 'Add program to comparison'
                                              }
                                              className={`inline-flex items-center gap-2 text-xs sm:text-sm font-semibold px-3.5 py-2 rounded-xl border transition-all duration-150 active:scale-[0.98] ${isProgramInCompare
                                                  ? 'border-primary-300 bg-primary-50 text-primary-700 hover:bg-primary-100/80 shadow-xs'
                                                  : isTrayFull
                                                    ? 'border-slate-200 bg-slate-50 text-slate-300 cursor-not-allowed'
                                                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:text-primary-600 hover:border-primary-300 shadow-xs group'
                                                }`}
                                            >
                                              {isProgramInCompare ? (
                                                <>
                                                  <CheckIcon className="h-4 w-4 text-primary-600 stroke-[2.5]" />
                                                  <span>Added to comparison</span>
                                                </>
                                              ) : (
                                                <>
                                                  <ArrowsRightLeftIcon className="h-4 w-4 text-slate-400 group-hover:text-primary-600 transition-colors" />
                                                  <span>{isTrayFull ? 'Comparison limit reached' : 'Add to comparison'}</span>
                                                  {compareItems.length > 0 && !isTrayFull && (
                                                    <span className="text-[10px] font-normal text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                                                      {compareItems.length}/4
                                                    </span>
                                                  )}
                                                </>
                                              )}
                                            </button>
                                          );
                                        })()}

                                        <div className="flex items-center gap-2.5 text-xs text-slate-400 font-medium">
                                          {program.updated_at && (
                                            <span
                                              className="flex items-center cursor-default"
                                              title={`Updated on ${formatDateDisplay(program.updated_at)}`}
                                            >
                                              <ArrowPathIcon className="h-3.5 w-3.5 mr-1 text-slate-400" />
                                              Last updated {formatRelativeTime(program.updated_at)}
                                            </span>
                                          )}
                                          {(program.source_url || (!university.is_partner && university.website)) && (
                                            <>
                                              {program.updated_at && <span className="text-slate-300">•</span>}
                                              <a
                                                href={program.source_url || university.website}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                                className="inline-flex items-center gap-1 text-slate-500 hover:text-primary-600 transition-colors group"
                                              >
                                                <span>Official Source</span>
                                                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 text-slate-400 group-hover:text-primary-600 transition-colors" />
                                              </a>
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Empty Search Result State */}
                  {totalProgramsCount > 0 && groupedPrograms.length === 0 && (
                    <div className="text-center py-12 bg-slate-50/75 rounded-2xl border border-dashed border-slate-200 my-2">
                      <AcademicCapIcon className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                      <h4 className="text-sm font-bold text-slate-700">No programs match your search</h4>
                      <p className="text-xs text-slate-400 mt-1">
                        No academic programs found matching <span className="font-semibold text-slate-600">"{programSearchQuery}"</span>
                        {selectedDegreeGroup !== 'all' && ' in this degree level'}.
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setProgramSearchQuery('');
                          setSelectedDegreeGroup('all');
                        }}
                        className="mt-3.5 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold text-primary-600 bg-primary-50 border border-primary-100 hover:bg-primary-100 transition-colors cursor-pointer"
                      >
                        <span>Reset Filters</span>
                      </button>
                    </div>
                  )}

                  {/* Total Zero Programs Empty State */}
                  {(!university.programs || university.programs.length === 0) && (
                    <div className="text-center py-8">
                      <p className="text-slate-500 text-sm sm:text-base font-semibold">
                        No programs available. Contact the university for more information.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Fee Structure Section */}
            <div id="fees" className="scroll-mt-32">
              <div className="card shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6">
                  <div>
                    <h3 className="text-xl font-extrabold text-slate-800">Fee Structure</h3>
                    <p className="text-xs sm:text-sm text-slate-500 mt-1">
                      General institutional charges and deposits. Program-specific tuition is listed above under Programs Offered.
                    </p>
                  </div>
                </div>
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 sm:px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Category</th>
                        <th className="px-4 sm:px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-100">
                      {university.feeStructure && university.feeStructure.length > 0 ? (
                        university.feeStructure.map((fee, index) => (
                          <tr key={index} className="hover:bg-slate-50 transition-colors">
                            <td className="px-4 sm:px-6 py-5 text-xs sm:text-sm font-bold text-slate-700 break-words">{fee.category}</td>
                            <td className="px-4 sm:px-6 py-5 text-xs sm:text-sm font-bold text-primary-600 break-words">
                              {formatFeeDisplay(fee.amount)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="2" className="px-4 sm:px-6 py-8 text-xs sm:text-sm text-slate-400 text-center font-medium">
                            Fee structure not published yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Admission Schedule Section */}
            <div id="deadlines" className="scroll-mt-32 card shadow-sm">
              <h3 className="text-xl font-extrabold text-slate-800 mb-6">Admission Schedule</h3>
              <div className="space-y-3">
                {university.deadlines && university.deadlines.length > 0 ? (
                  university.deadlines.map((deadline, index) => (
                    <div key={index} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border border-slate-200 rounded-xl bg-white hover:bg-white hover:shadow-md transition-premium gap-4">
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-800 text-sm sm:text-base break-words">
                          {deadline.degree
                            ? formatProgramDisplay(deadline.program, deadline.degree)
                            : deadline.program
                          }
                        </h4>
                        <div className="flex items-center text-xs font-semibold text-slate-500 mt-1.5">
                          <CalendarIcon className="h-4 w-4 mr-1 text-slate-400" />
                          <span>Deadline: {formatDateDisplay(deadline.deadline)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {(() => {
                          const dynStatus = computeDynamicDeadlineStatus(deadline.deadline, deadline.status);
                          const isOp = dynStatus.toLowerCase() === 'open';
                          const isUp = dynStatus.toLowerCase() === 'upcoming';
                          return (
                            <span className={`px-2.5 py-0.5 rounded-lg text-xs font-bold flex-shrink-0 ${isOp
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                                : isUp
                                  ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                  : 'bg-rose-50 text-rose-700 border border-rose-100'
                              }`}>
                              {dynStatus}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-slate-400 text-center py-5 text-sm font-semibold">No application deadlines available</p>
                )}
              </div>
            </div>

            {/* FAQs Section */}
            {!isStaticUniversity && university.faqs && university.faqs.length > 0 && (
              <div id="faqs" className="scroll-mt-32 card shadow-sm">
                <h3 className="text-xl font-extrabold text-slate-800 mb-6">Frequently Asked Questions</h3>
                <div className="space-y-3">
                  {university.faqs.map((faq, index) => {
                    const isOpen = !!expandedFaq[index];
                    return (
                      <div key={index} className="border border-slate-200 rounded-xl overflow-hidden bg-white hover:bg-white transition-premium">
                        <button
                          onClick={() => setExpandedFaq(prev => ({ ...prev, [index]: !prev[index] }))}
                          className="w-full flex items-center justify-between p-4 text-left focus:outline-none"
                        >
                          <span className="font-bold text-slate-700 text-sm sm:text-base">{faq.question}</span>
                          <ChevronDownIcon className={`h-5 w-5 text-slate-400 transition-transform duration-300 ${isOpen ? 'rotate-180 text-primary-500' : ''}`} />
                        </button>
                        <div className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[500px] border-t border-slate-100 p-4' : 'max-h-0 overflow-hidden'}`}>
                          <p className="text-slate-600 text-xs sm:text-sm leading-relaxed whitespace-pre-line">{faq.answer}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Campus Info Section */}
            <div id="campus" className="scroll-mt-32 space-y-6">
              {/* Campus Facilities Overview */}
              {university.facilities && university.facilities.length > 0 && (
                <div className="card shadow-sm">
                  <h3 className="text-xl font-extrabold text-slate-800 mb-6">Campus Facilities Overview</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {university.facilities.map((facility, index) => (
                      <div key={index} className="flex items-center p-3.5 bg-slate-50 rounded-xl border border-slate-200 hover:bg-white hover:border-primary-200 transition-premium hover:shadow-sm hover:-translate-y-0.5">
                        <span className="h-2 w-2 rounded-full bg-primary-500 mr-3 shrink-0" />
                        <span className="text-xs sm:text-sm font-bold text-slate-700 break-words">{facility}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Detailed Campus Facilities */}
              {loadingFacilities ? (
                <div className="card flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                </div>
              ) : (
                university.campus_facilities && university.campus_facilities.length > 0 && (
                  <div className="card shadow-sm">
                    <h3 className="text-xl font-extrabold text-slate-800 mb-6">Campus Facilities Details</h3>
                    <div className="space-y-3">
                      {university.campus_facilities.map((facility, index) => {
                        const isOpen = !!expandedFacility[index];
                        return (
                          <div key={index} className="border border-slate-200 rounded-xl overflow-hidden bg-white hover:bg-white hover:border-primary-200 hover:bg-white transition-premium">
                            <button
                              onClick={() => setExpandedFacility(prev => ({ ...prev, [index]: !prev[index] }))}
                              className="w-full flex items-center justify-between p-4 text-left focus:outline-none"
                            >
                              <span className="font-bold text-slate-700 text-sm sm:text-base">
                                {facility.name || facility.facility_name}
                              </span>
                              <ChevronDownIcon className={`h-5 w-5 text-slate-400 transition-transform duration-300 ${isOpen ? 'rotate-180 text-primary-500' : ''}`} />
                            </button>
                            <div className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[500px] border-t border-slate-100 p-4' : 'max-h-0 overflow-hidden'}`}>
                              <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                                {facility.description || facility.facility_description || 'No description available.'}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>

          {/* Sidebar Column */}
          <div className="space-y-6">
            <div className="lg:sticky lg:top-[140px] space-y-6">
              {/* Quick Info & Contact Info Merged */}
              <div className="card shadow-sm">
                <h3 className="text-lg font-extrabold text-slate-800 mb-5">Quick Information</h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="h-10 w-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center mr-3.5 flex-shrink-0">
                      <MapPinIcon className="h-5 w-5" />
                    </div>
                    <div>
                      <span className="text-xs font-bold text-slate-400 block uppercase tracking-wider">Location</span>
                      <span className="text-slate-700 text-sm font-semibold leading-relaxed break-words">{university.address || university.location}</span>
                    </div>
                  </div>

                  {/* <div className="flex items-start">
  <div className="h-10 w-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center mr-3.5 flex-shrink-0">
  <AcademicCapIcon className="h-5 w-5" />
  </div>
  <div>
  <span className="text-xs font-bold text-slate-400 block uppercase tracking-wider">Type</span>
  <span className="text-slate-700 text-sm font-semibold leading-relaxed">{university.type} University</span>
  </div>
  </div>

  <div className="flex items-start">
  <div className="h-10 w-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center mr-3.5 flex-shrink-0">
  <CalendarIcon className="h-5 w-5" />
  </div>
  <div>
  <span className="text-xs font-bold text-slate-400 block uppercase tracking-wider">Established</span>
  <span className="text-slate-700 text-sm font-semibold leading-relaxed">{university.established}</span>
  </div>
  </div> */}

                  {university.phone && (
                    <div className="flex items-start">
                      <div className="h-10 w-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center mr-3.5 flex-shrink-0">
                        <PhoneIcon className="h-5 w-5" />
                      </div>
                      <div>
                        <span className="text-xs font-bold text-slate-400 block uppercase tracking-wider">Phone</span>
                        <span className="text-slate-700 text-sm font-semibold leading-relaxed break-all">{university.phone}</span>
                      </div>
                    </div>
                  )}

                  {university.email && (
                    <div className="flex items-start">
                      <div className="h-10 w-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center mr-3.5 flex-shrink-0">
                        <EnvelopeIcon className="h-5 w-5" />
                      </div>
                      <div>
                        <span className="text-xs font-bold text-slate-400 block uppercase tracking-wider">Email</span>
                        <span className="text-slate-700 text-sm font-semibold leading-relaxed break-all">{university.email}</span>
                      </div>
                    </div>
                  )}

                  {university.website && (
                    <div className="pt-4 border-t border-slate-100 mt-2">
                      <a
                        href={university.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full btn-secondary text-sm py-2.5 flex items-center justify-center gap-2"
                      >
                        <GlobeAltIcon className="h-4 w-4" />
                        Visit University Website
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Program Selection Modal */}
      {showProgramModal && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 transition-all duration-300 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col border border-slate-200">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-5 flex items-center justify-between z-10">
              <div className="flex flex-col">
                <h2 className="text-xl font-extrabold text-slate-800 leading-snug">Select a Program</h2>
                <p className="text-xs text-slate-500 font-medium mt-0.5">{university.name || university.university_name}</p>
              </div>
              <button
                onClick={() => {
                  setShowProgramModal(false);
                  setSelectedProgram(null);
                }}
                className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                aria-label="Close modal"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {availablePrograms.length === 0 ? (
                <div className="text-center py-12 flex flex-col items-center">
                  <AcademicCapIcon className="h-12 w-12 text-slate-300 mb-3" />
                  <p className="text-slate-500 text-sm font-semibold">No programs currently listed for direct apply.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {availablePrograms.map((program) => {
                    const isSelected = selectedProgram?.id === program.id;
                    return (
                      <div
                        key={program.id}
                        onClick={() => setSelectedProgram(program)}
                        className={`border rounded-xl p-4 cursor-pointer transition-premium flex items-start gap-4 ${isSelected
                            ? 'border-primary-500 bg-primary-50/40 shadow-sm shadow-primary-500/5'
                            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50 bg-white'
                          }`}
                      >
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-slate-800 text-base leading-snug">
                            {program.canonical_name || program.name || program.program_name}
                          </h3>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 mt-1 font-semibold">
                            <span>{program.degree_type?.name || program.degree_type || 'Degree'}</span>
                            {program.duration && (
                              <>
                                <span className="text-slate-300">•</span>
                                <span>{program.duration}</span>
                              </>
                            )}
                          </div>
                          {program.description && (
                            <p className="text-xs text-slate-600 mt-2.5 leading-relaxed line-clamp-2">
                              {program.description}
                            </p>
                          )}
                          {program.program_fee && (
                            <p className="text-xs font-semibold text-primary-600 mt-2.5 bg-primary-50/80 inline-block px-2.5 py-0.5 rounded-lg border border-primary-100/30">
                              Fee: PKR {program.program_fee.toLocaleString()}
                            </p>
                          )}
                        </div>
                        <div className="flex-shrink-0 mt-0.5">
                          <div
                            className={`w-5 h-5 rounded-full border flex items-center justify-center transition-all ${isSelected
                                ? 'border-primary-600 bg-primary-600 text-white'
                                : 'border-slate-300 bg-white'
                              }`}
                          >
                            {isSelected && (
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-slate-50 border-t border-slate-100 px-6 py-4 flex items-center justify-end gap-3 z-10">
              <button
                onClick={() => {
                  setShowProgramModal(false);
                  setSelectedProgram(null);
                }}
                className="btn-secondary text-sm px-4 py-2"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitApplication}
                disabled={!selectedProgram || submittingApplication}
                className={`btn-primary text-sm px-5 py-2 flex items-center justify-center ${!selectedProgram || submittingApplication
                    ? 'opacity-40 cursor-not-allowed bg-slate-300 hover:from-slate-300 hover:to-slate-300 text-slate-500 shadow-none border-none'
                    : ''
                  }`}
              >
                {submittingApplication ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Submitting...
                  </span>
                ) : 'Next'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Additional Admission Questions Modal */}
      {showAdmissionQuestionsModal && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 transition-all duration-300">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col border border-slate-200 animate-fade-in">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-5 flex items-center justify-between z-10">
              <div>
                <h2 className="text-xl font-extrabold text-slate-800 leading-snug">Additional Admission Information</h2>
                <p className="text-xs text-slate-500 font-semibold mt-0.5">Please answer the following questions to proceed</p>
              </div>
              <button
                onClick={() => {
                  setShowAdmissionQuestionsModal(false);
                  setShowProgramModal(true); // Go back to program selection
                }}
                className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                aria-label="Close modal"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {loadingQuestions ? (
                <div className="text-center py-12">
                  <div className="animate-spin h-10 w-10 border-b-2 border-primary-600 rounded-full mx-auto mb-4"></div>
                  <p className="text-slate-500 text-sm font-semibold">Loading questions...</p>
                </div>
              ) : (
                <div className="space-y-5">
                  {admissionQuestions.map((question, index) => (
                    <div key={question.id} className="border-b border-slate-100 pb-5 last:border-b-0 last:pb-0">
                      <label className="block text-sm font-bold text-slate-700 mb-2 leading-relaxed">
                        {index + 1}. {question.question_label}
                        {question.is_required && <span className="text-red-500 ml-1 font-bold">*</span>}
                      </label>

                      {question.question_type === 'text' && (
                        <input
                          type="text"
                          value={admissionResponses[question.id] || ''}
                          onChange={(e) => handleAdmissionResponseChange(question.id, e.target.value)}
                          className="w-full px-4 py-2.5 bg-slate-50 hover:bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-xl text-sm text-slate-700 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-medium"
                          placeholder="Enter your answer"
                          required={question.is_required}
                        />
                      )}

                      {question.question_type === 'number' && (
                        <input
                          type="number"
                          value={admissionResponses[question.id] || ''}
                          onChange={(e) => handleAdmissionResponseChange(question.id, e.target.value)}
                          className="w-full px-4 py-2.5 bg-slate-50 hover:bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-xl text-sm text-slate-700 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-medium"
                          placeholder="Enter a number"
                          required={question.is_required}
                        />
                      )}

                      {question.question_type === 'dropdown' && (
                        <div className="relative">
                          <select
                            value={admissionResponses[question.id] || ''}
                            onChange={(e) => handleAdmissionResponseChange(question.id, e.target.value)}
                            className="w-full pl-4 pr-10 py-2.5 bg-slate-50 hover:bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-xl text-sm text-slate-700 font-semibold focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all cursor-pointer appearance-none"
                            required={question.is_required}
                          >
                            <option value="">Select an option</option>
                            {(question.options || []).map((option, idx) => (
                              <option key={idx} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-400">
                            <ChevronDownIcon className="h-5 w-5" />
                          </div>
                        </div>
                      )}

                      {question.question_type === 'yes_no' && (
                        <div className="flex space-x-6 mt-1">
                          <label className="flex items-center space-x-2.5 cursor-pointer group select-none">
                            <input
                              type="radio"
                              name={`question_${question.id}`}
                              value="Yes"
                              checked={admissionResponses[question.id] === 'Yes'}
                              onChange={(e) => handleAdmissionResponseChange(question.id, e.target.value)}
                              className="w-4 h-4 text-primary-600 focus:ring-primary-500/20 border-slate-300 cursor-pointer"
                              required={question.is_required}
                            />
                            <span className="text-slate-700 font-semibold text-sm group-hover:text-slate-900 transition-colors">Yes</span>
                          </label>
                          <label className="flex items-center space-x-2.5 cursor-pointer group select-none">
                            <input
                              type="radio"
                              name={`question_${question.id}`}
                              value="No"
                              checked={admissionResponses[question.id] === 'No'}
                              onChange={(e) => handleAdmissionResponseChange(question.id, e.target.value)}
                              className="w-4 h-4 text-primary-600 focus:ring-primary-500/20 border-slate-300 cursor-pointer"
                              required={question.is_required}
                            />
                            <span className="text-slate-700 font-semibold text-sm group-hover:text-slate-900 transition-colors">No</span>
                          </label>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-slate-50 border-t border-slate-100 px-6 py-4 flex items-center justify-end gap-3 z-10">
              <button
                onClick={() => {
                  setShowAdmissionQuestionsModal(false);
                  setShowProgramModal(true); // Go back to program selection
                }}
                className="btn-secondary text-sm px-4 py-2"
              >
                Back
              </button>
              <button
                onClick={handleAdmissionQuestionsSubmit}
                disabled={submittingApplication}
                className={`btn-primary text-sm px-5 py-2 flex items-center justify-center ${submittingApplication
                    ? 'opacity-40 cursor-not-allowed bg-slate-300 hover:from-slate-300 hover:to-slate-300 text-slate-500 shadow-none border-none'
                    : ''
                  }`}
              >
                {submittingApplication ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Submitting...
                  </span>
                ) : 'Submit Application'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UniversityDetail;
